/* ── 127 §2 — NEVER OVERWRITE. CANCEL, OR A NUMBERED VERSION. ───────────────
 *
 * RULED (Laurent): "do not overwrite, offer to cancel or add a numbered version
 * (like in Downloads)."
 *
 * MOVED HERE VERBATIM FROM `TaskDocsPane.tsx:748-804@21714e16^` BY 129 §3 — the
 * coordinate is as-of the commit BEFORE the move, because after it that file
 * ends at :766 and the lines are here instead. It is where
 * a SECOND mount first appeared — the drop files a document too, and 129's fence
 * says "127 §2's collision ruling is settled … Reuse it; do not re-decide it."
 * Reusing a ruling means mounting its dialog, not writing a second one that
 * agrees with it today. Same title, same body, same two buttons, same zIndex;
 * the only change is that `conflict` and the two acts arrive as props.
 *
 * THERE IS NO THIRD BUTTON AND THERE CANNOT BE ONE. `replace` is not a choice
 * this dialog withholds — `uploadFileByPath` (m365.js:2326) takes no
 * `conflictBehavior` argument at all, and both uploaders beneath it default to
 * `'fail'`. So the fence is enforced by the absence of a parameter rather than
 * by this component's restraint.
 *
 * THE NUMBER IS THE DRIVE'S, NOT OURS. `suggestion` is what the server would use
 * RIGHT NOW, and it is shown so the reader knows what they are agreeing to — but
 * the second attempt does not send it back. It sends `onConflict: 'version'`,
 * and the route walks (1), (2), … with each attempt carrying
 * `conflictBehavior: 'fail'` so the DRIVE decides atomically. That is §2's "ask
 * the drive, not count locally": two people dropping at once cannot both be
 * given (1), because the loser's own upload is what refuses.
 *
 * `zIndex 400` — it opens OVER the document dialog's 300, the same convention
 * `openOneDrivePicker` uses from the same place (109 §1a's ladder: overlay 100 ·
 * stacked dialog 300 · imperative pickers 400).
 */
import { useTranslation } from "../i18n";
import { ModalLayout, SaveButton, CancelButton } from "./SharedUI";
import { FS, Z } from "../lib/tokens";

/** What `matterFiles.name_conflict` gives a caller, plus the bytes it is still
 *  holding. The FILE rides along because re-asking the OS for it would be a
 *  second file dialog for a question the user has already answered. */
export type MatterFileConflictState = {
  file: File;
  name: string;
  suggestion: string;
};

export default function MatterFileConflictDialog({
  conflict, busy, onVersion, onCancel,
}: {
  conflict: MatterFileConflictState;
  busy?: boolean;
  onVersion: (c: MatterFileConflictState) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  return (
    <ModalLayout
      title={t("matterFiles.conflict_title", {
        fallback: "That name is already filed here",
      })}
      onClose={onCancel}
      zIndex={Z.folderPicker}
      maxWidth={440}
      footer={
        <div className="modal-footer">
          <SaveButton
            label={t("matterFiles.conflict_version", {
              fallback: "Save as a numbered version",
            })}
            onClick={() => onVersion(conflict)}
            disabled={!!busy}
          />
          <CancelButton onClick={onCancel} />
        </div>
      }
    >
      <p style={{ fontSize: FS.s11, color: "var(--tp)", margin: 0 }}>
        {t("matterFiles.conflict_body", {
          name: conflict.name,
          suggestion: conflict.suggestion,
          fallback: `“${conflict.name}” is already in this matter's files. Nothing will be overwritten — the file can be filed as “${conflict.suggestion}” instead.`,
        })}
      </p>
    </ModalLayout>
  );
}
